import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, TextField, Button, Divider, Alert, Snackbar, } from '@mui/material';
import { useAuth } from '../context/AuthContext';

import { Autocomplete, Tooltip } from "@mui/material";
import { toProperCase, toSentenceCase } from '../utils/formatRows';
import { getCurrentFYear } from '../utils/financialYear';

// Create a fresh axios instance without auth interceptors for external APIs
const axiosNoAuth = axios.create();

// const path = `http://103.251.218.254:91/api/api`

const HsnMaster = ({ id, mode, onPopupCancel, connectionString, companyId: propsCompanyId }) => {
    const { user, selectedCompany } = useAuth();
    // const { id } = useParams();
    const { id: routeId } = useParams();
    const recordId =
        mode === 'edit'
            ? id           // dialog edit → HSNId
            : routeId;
    const navigate = useNavigate();
    const [formError, setFormError] = useState('');
    const [HSNNo, setHSNNo] = useState('');
    const [HSNDescription, setHSNDescription] = useState('');
    const path = `${connectionString}/api`;
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const AppRegID = selectedCompany?.AppRegID || null;

    const [hsnOptions, setHsnOptions] = useState([]);
    const [hsnLoading, setHsnLoading] = useState(false);

    // 🔁 New replacements:
    const userId = user?.UserId || null;
    const companyId = (mode === 'comp' || mode === 'edit')
        ? propsCompanyId              // Use props when called from HSNModule
        : selectedCompany?.CompId;
    const fYear = getCurrentFYear();
    const branchId = selectedCompany?.BranchId || null;
    console.log(companyId)



    const requiredAsteriskStyle = {
        '& .MuiFormLabel-asterisk': {
            color: 'red',
        },
    };


    useEffect(() => {
        if (!HSNNo) {
            setHsnOptions([]);
            return;
        }

        const fetchHSN = async () => {
            try {
                setHsnLoading(true);

                // Use axiosNoAuth to avoid sending x-session-token header to external API
                // const res = await axiosNoAuth.get(`http://103.133.215.205:82/api/api/hsn`, {
                    const res = await axiosNoAuth.get(`https://services.reonbook.com/api/hsn`, {
                    params: { code: HSNNo }
                });
                console.log(res);

                setHsnOptions(res.data?.data || []);
            } catch (err) {
                console.error("HSN search error", err);
            } finally {
                setHsnLoading(false);
            }
        };

        fetchHSN();
    }, [HSNNo]);

    useEffect(() => {
        if (mode === 'edit' && recordId) {
            const fetchData = async () => {
                try {
                    const response = await fetch(`${path}/hsn/getDetails/${recordId}`);
                    const data = await response.json();
                    setHSNNo(data.HSNNo);
                    setHSNDescription(data.HSNDescription);
                } catch (error) {
                    console.error('Error fetching HSN data:', error);
                }
            };
            fetchData();
        }
    }, [recordId, mode, path]);

    const funSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        try {
            if (!HSNNo.trim()) {
                setFormError('HSN No. is required');
                return;
            }

            if (mode === 'add' || mode === 'comp') {
                await axios.post(`${path}/hsn/Insert`, {
                    HSNNo,
                    HSNDescription,
                    companyId,
                    AppRegID,
                    userId,
                    fYear,
                    branchId,
                });
                setHSNNo('');
                setHSNDescription('');
                setSnackbar({ open: true, message: 'Data inserted successfully', severity: 'success' });

                if (onPopupCancel) {
                    setTimeout(() => { onPopupCancel() }, 600)
                }
            } else if (mode === 'edit') {
                await axios.put(`${path}/hsn/Update/${recordId}`, {
                    HSNNo,
                    HSNDescription,
                    companyId,
                    AppRegID,
                    editUserId: userId,
                });
                setSnackbar({ open: true, message: 'Data updated successfully', severity: 'success' });
                if (onPopupCancel) {
                    setTimeout(() => { onPopupCancel() }, 600)
                }
            }
        } catch (error) {
            console.error('Error inserting/updating data:', error);
            setSnackbar({ open: true, message: `Error ${mode === 'add' || mode === 'comp' ? 'inserting' : 'updating'} data`, severity: 'error' });
        }
    };

    const funReset = () => {
        if (onPopupCancel) {
            onPopupCancel();
        } else {
            setFormError('');
            setHSNNo('');
            setHSNDescription('');
            navigate('/main/hsnDetail');
        }
    };

    // ──────────────── Alt+S (Save/Update) & Alt+C (Cancel) shortcuts ────────────────
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey) return;

            if (e.code === "KeyS") {
                e.preventDefault();
                funSubmit(e);
            } else if (e.code === "KeyC") {
                e.preventDefault();
                funReset();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    return (
        <>
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)' }} />
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                    {mode === 'add' || mode === 'comp' ? 'Add New HSN Code' : 'Edit HSN Code'}
                </Typography>
            </Box>
            <Divider />
            <Box component="form" onSubmit={funSubmit} noValidate>
                <Box sx={{ px: 2.5, pt: 2, pb: 0.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* <TextField
                        fullWidth
                        label="HSN No."
                        value={HSNNo}
                        size='small'
                        onChange={(e) => setHSNNo(e.target.value)}
                        inputProps={{ maxLength: 8 }}
                        required
                        InputLabelProps={{ sx: requiredAsteriskStyle }}
                        sx={{
                            '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' },
                            '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                        }}
                    /> */}

                    <Autocomplete
                        freeSolo
                        options={hsnOptions}
                        loading={hsnLoading}
                        getOptionLabel={(option) =>
                            typeof option === "string" ? option : option.code || ""
                        }
                        value={hsnOptions.find(o => o.code === HSNNo) || null}

                        onInputChange={(e, newValue) => {
                            setHSNNo(newValue);
                        }}

                        onChange={(e, value) => {
                            if (value) {
                                setHSNNo(value.code);
                            }
                        }}

                        renderOption={(props, option) => {
                            const isLong = option.description.length > 70;

                            const shortDesc = isLong
                                ? option.description.slice(0, 70) + "..."
                                : option.description;

                            const content = (
                                <Box>
                                    <Typography fontWeight={600}>
                                        {option.code}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    {toSentenceCase(shortDesc)}
                                    </Typography>
                                </Box>
                            );


                            return (
                                <li {...props}>
                                    {isLong ? (
                                        <Tooltip title={option.description} placement="right">
                                            {content}
                                        </Tooltip>
                                    ) : (
                                        content
                                    )}
                                </li>
                            );
                        }}

                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="HSN No."
                                size="small"
                                required
                                InputLabelProps={{ sx: requiredAsteriskStyle }}
                            />
                        )}
                    />

                    <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={3}
                        value={HSNDescription}
                        onChange={(e) => setHSNDescription(e.target.value)}
                        inputProps={{ maxLength: 50 }}
                    />

                    {formError && (
                        <Alert severity="error">{formError}</Alert>
                    )}

                </Box>
                <Divider sx={{ mt: 1.5 }} />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button type="button" variant="outlined" color="inherit" size="small" onClick={funReset}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" size="small">
                        {mode === 'add' || mode === 'comp' ? 'Save' : 'Update'}
                    </Button>
                </Box>
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default HsnMaster;